import { Box, useTheme } from '@mui/material';
import clsx from 'clsx';
import React, { PropsWithChildren, useMemo } from 'react';

import type { Theme } from '../../themes/themes';
import { FinalContainer } from '../FinalGrid/FinalGrid';
import { Typography } from '../Typography/Typography';
import { getStyles } from './Opener.styles';

export type Layout =
  | 'homeImageRight'
  | 'homeImageLeft'
  | 'deviceImageRight'
  | 'deviceImageLeft'
  | 'productPage'
  | 'productPageSmall'
  | 'servicePage'
  | 'serviceAndCampaignPage';

export type MediaFill = 'dark' | 'standard' | 'light' | 'none';

export const mediaFillLabelToOpacityValue: { [key in MediaFill]: string } = {
  dark: '49%',
  standard: '64%',
  light: '79%',
  none: '100%',
};

export const getOpacityForMediaFillValue = (value: MediaFill = 'standard'): string =>
  mediaFillLabelToOpacityValue[value] || mediaFillLabelToOpacityValue['standard'];

export interface OpenerProps {
  children: React.ReactNode;
  layout: Layout;
}

export const Opener: React.FC<OpenerProps> = ({ children, layout }) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);
  return (
    <Box
      sx={styles.container[layout]}
      className={clsx('PortalsOpener-container', `PortalsOpener-${layout}`)}
      display="grid"
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child) ? React.cloneElement(child as React.ReactElement<OpenerProps>, { layout }) : child
      )}
    </Box>
  );
};

interface OpenerMediaProps {
  children: React.ReactNode;
  mediaFill: MediaFill;
}

export const OpenerMedia: React.FC<OpenerMediaProps> = ({
  children,
  mediaFill,
  layout,
}: OpenerMediaProps & { layout: OpenerProps['layout'] }) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);
  return (
    <Box sx={styles.media[layout]} className={clsx('PortalsOpener-media')} gridArea="media">
      <Box
        sx={{
          ...styles.innerMedia[layout],
          '& img': {
            ...styles.media[layout]['& img'],
            opacity: { xs: '100%', md: getOpacityForMediaFillValue(mediaFill) },
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export type OpenerHeadProps = PropsWithChildren<{
  overline?: string;
  headline?: string;
}>;

/**
 * OpenerHead: Adding a sticky headline element to opener
 *
 * Either overline/headline or children is required
 *
 * @param overline Overline text
 * @param headline Headline text
 * @param children RichText Headline text
 * @param layout Layout of the opener (passed by prop drilling via <Opener layout />)
 * @constructor
 */
export const OpenerHead: React.FC<OpenerHeadProps> = ({
  overline,
  headline,
  children,
  layout,
}: PropsWithChildren<OpenerHeadProps> & { layout: OpenerProps['layout'] }) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);
  const variant = ['productPage', 'servicePage', 'serviceAndCampaignPage'].includes(layout) ? 'openerFullPage' : 'h1';

  return (
    <Box
      className={clsx('PortalsOpener-head', `PortalsOpener-head-${layout}`)}
      gridArea="head"
      display="flex"
      flexDirection="column"
      sx={styles.head[layout]}
    >
      {overline ? (
        <Typography
          component="div"
          variant="h5"
          innerPadding="horizontal"
          color="unset"
          className={clsx('PortalsOpener-head-overline')}
        >
          {overline}
        </Typography>
      ) : null}
      {headline ? (
        <Typography
          variant={variant}
          hyphens="none"
          wordBreak="normal"
          innerPadding="horizontal"
          color="unset"
          className={clsx('PortalsOpener-head-headline')}
        >
          {headline}
        </Typography>
      ) : null}
      {children && children}
    </Box>
  );
};

interface OpenerBodyProps {
  children: React.ReactNode;
}

export const OpenerBody: React.FC<OpenerBodyProps> = ({
  children,
  layout,
}: OpenerBodyProps & { layout: OpenerProps['layout'] }) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);

  return (
    <Box
      className={clsx('PortalsOpener-body')}
      gridArea="body"
      display="flex"
      flexDirection="column"
      sx={styles.body[layout]}
    >
      {children}
    </Box>
  );
};

interface OpenerFootProps {
  children: React.ReactNode;
}

export const OpenerFoot: React.FC<OpenerFootProps> = ({
  children,
  layout,
}: OpenerFootProps & { layout: OpenerProps['layout'] }) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);

  return (
    <Box
      className={clsx('PortalsOpener-footer')}
      gridArea="foot"
      display="flex"
      flexDirection="column"
      sx={styles.foot[layout]}
    >
      {children}
    </Box>
  );
};

interface OpenerContentProps {
  children: React.ReactNode;
  disableHorizontalPadding?: boolean;
}

export const OpenerContent: React.FC<OpenerContentProps> = ({
  children,
  layout,
  disableHorizontalPadding = false,
}: OpenerContentProps & { layout: OpenerProps['layout'] }) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);
  const padding = useMemo(
    () => (disableHorizontalPadding ? { paddingLeft: 0, paddingRight: 0 } : {}),
    [disableHorizontalPadding]
  );

  return (
    <FinalContainer
      className={clsx('PortalsOpener-content', `PortalsOpener-content-${layout}`)}
      gridArea="content"
      sx={styles.content[layout]}
      {...padding}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child) ? React.cloneElement(child as React.ReactElement<OpenerProps>, { layout }) : child
      )}
    </FinalContainer>
  );
};

interface OpenerActionsProps {
  children: React.ReactNode;
}

export const OpenerActions: React.FC<OpenerActionsProps> = ({
  children,
  layout,
}: OpenerActionsProps & { layout: OpenerProps['layout'] }) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);

  return (
    <FinalContainer
      className={clsx('PortalsOpener-actions', `PortalsOpener-actions-${layout}`)}
      gridArea="actions"
      disableGutters={true}
      sx={styles.actions[layout]}
      zIndex={1}
    >
      {children}
    </FinalContainer>
  );
};
